import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getConversations, getMessages, sendMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, User, ArrowLeft, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, isToday, isYesterday } from 'date-fns';

const MessagesPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(searchParams.get('user') || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (selectedUserId) {
      fetchMessages(selectedUserId);
    }
  }, [selectedUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const data = await getConversations();
      setConversations(data);
      
      // If there's a user param and they're not in conversations, still select them
      const userParam = searchParams.get('user');
      if (userParam && !data.find(c => c.user_id === userParam)) {
        setSelectedUserId(userParam);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const data = await getMessages(userId);
      setMessages(data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUserId) return;
    
    setSending(true);
    try {
      await sendMessage({
        receiver_id: selectedUserId,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedUserId);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday ' + format(date, 'h:mm a');
    } else {
      return format(date, 'MMM d, h:mm a');
    }
  };

  const selectedConversation = conversations.find(c => c.user_id === selectedUserId);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-[#596652]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-semibold text-[#2C3329] mb-6">Messages</h1>

        <div className="bg-white rounded-2xl border border-[#EBE8DF] overflow-hidden h-[calc(100vh-200px)]">
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full sm:w-80 border-r border-[#EBE8DF] ${selectedUserId ? 'hidden sm:block' : ''}`}>
              <div className="p-4 border-b border-[#EBE8DF]">
                <h2 className="font-medium text-[#2C3329]">Conversations</h2>
              </div>
              <ScrollArea className="h-[calc(100%-57px)]">
                {conversations.length > 0 ? (
                  <div className="divide-y divide-[#EBE8DF]">
                    {conversations.map((conv) => (
                      <button
                        key={conv.user_id}
                        onClick={() => setSelectedUserId(conv.user_id)}
                        className={`w-full p-4 text-left hover:bg-[#FDFBF7] transition-colors ${
                          selectedUserId === conv.user_id ? 'bg-[#8C9A76]/10' : ''
                        }`}
                        data-testid={`conversation-${conv.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#8C9A76] rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-[#2C3329] truncate">{conv.user_name}</span>
                              {conv.unread_count > 0 && (
                                <span className="w-5 h-5 bg-[#D97757] text-white text-xs rounded-full flex items-center justify-center">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#596652] truncate">{conv.last_message}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-[#EBE8DF] mx-auto mb-3" />
                    <p className="text-[#596652] text-sm">No conversations yet</p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Messages Area */}
            <div className={`flex-1 flex flex-col ${!selectedUserId ? 'hidden sm:flex' : ''}`}>
              {selectedUserId ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-[#EBE8DF] flex items-center gap-3">
                    <button
                      onClick={() => setSelectedUserId(null)}
                      className="sm:hidden p-2 hover:bg-[#FDFBF7] rounded-lg"
                      data-testid="back-to-conversations"
                    >
                      <ArrowLeft className="w-5 h-5 text-[#596652]" />
                    </button>
                    <div className="w-10 h-10 bg-[#8C9A76] rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-medium text-[#2C3329]">
                      {selectedConversation?.user_name || 'User'}
                    </span>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-2 ${
                              msg.sender_id === user?.id
                                ? 'bg-[#D97757] text-white rounded-2xl rounded-br-md'
                                : 'bg-[#FDFBF7] border border-[#EBE8DF] text-[#2C3329] rounded-2xl rounded-bl-md'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user?.id ? 'text-white/70' : 'text-[#596652]'
                            }`}>
                              {formatMessageTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-[#EBE8DF]">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="bg-[#D97757] text-white rounded-xl hover:bg-[#C56547]"
                        data-testid="send-message-button"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 text-[#EBE8DF] mx-auto mb-4" />
                    <p className="text-[#596652]">Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
